import { useState, useEffect, useRef, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, LayerGroup, useMap, Polyline, Polygon, useMapEvents } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { cn } from "../lib/utils";
import { useLanguage } from "../components/LanguageProvider";
import { motion, AnimatePresence } from "framer-motion";
import L from "leaflet";
import jsPDF from "jspdf";
import "leaflet/dist/leaflet.css";

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom icons
const createCustomIcon = (color) => {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      background-color: ${color};
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

// Mock location data - St. Hubert, Germany and surrounding areas
const mockLocations = [
  { id: 1, name: "St. Hubert Zentrum", lat: 51.3833, lng: 6.4667, type: "organization", intensity: 85, events: 12 },
  { id: 2, name: "Kempen", lat: 51.3644, lng: 6.4189, type: "organization", intensity: 62, events: 8 },
  { id: 3, name: "Krefeld Hauptbahnhof", lat: 51.3208, lng: 6.5700, type: "location", intensity: 45, events: 24 },
  { id: 4, name: "Viersen Industriegebiet", lat: 51.2547, lng: 6.3914, type: "hotspot", intensity: 92, events: 45 },
  { id: 5, name: "Mönchengladbach", lat: 51.1805, lng: 6.4428, type: "location", intensity: 38, events: 15 },
  { id: 6, name: "Düsseldorf Flughafen", lat: 51.2895, lng: 6.7668, type: "organization", intensity: 55, events: 6 },
  { id: 7, name: "Nettetal", lat: 51.3167, lng: 6.2833, type: "hotspot", intensity: 71, events: 28 },
  { id: 8, name: "Tönisvorst", lat: 51.3208, lng: 6.4917, type: "location", intensity: 48, events: 19 },
];

const typeColors = {
  organization: "hsl(182, 72%, 45%)",
  location: "hsl(205, 80%, 55%)",
  hotspot: "hsl(0, 72%, 51%)",
};

// Map controller component
const MapController = ({ center, zoom, onMapReady }) => {
  const map = useMap();
  
  useEffect(() => {
    map.setView(center, zoom);
    if (onMapReady) onMapReady(map);
  }, [center, zoom, map, onMapReady]);
  
  return null;
};

// Map click handler for drawing and measuring
const MapClickHandler = ({ 
  measureMode, 
  drawMode, 
  directionsMode,
  onMeasureClick, 
  onDrawClick,
  onDirectionsClick 
}) => {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      if (measureMode) {
        onMeasureClick([lat, lng]);
      } else if (drawMode) {
        onDrawClick([lat, lng]);
      } else if (directionsMode) {
        onDirectionsClick([lat, lng]);
      }
    },
  });
  return null;
};

export default function GISMapView() {
  const { t } = useLanguage();
  const mapRef = useRef(null);
  const [locations, setLocations] = useState(mockLocations);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState([51.3833, 6.4667]); // St. Hubert, Germany
  const [mapZoom, setMapZoom] = useState(11);
  const [mapStyle, setMapStyle] = useState("light"); // Default to light
  const [layers, setLayers] = useState({
    markers: true,
    heatmap: true,
    connections: false,
  });
  const [clusteringEnabled, setClusteringEnabled] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [measureMode, setMeasureMode] = useState(false);
  const [drawMode, setDrawMode] = useState(false);
  const [directionsMode, setDirectionsMode] = useState(false);
  const [measurePoints, setMeasurePoints] = useState([]);
  const [polygonPoints, setPolygonPoints] = useState([]);
  const [directionPoints, setDirectionPoints] = useState([]);
  const [totalDistance, setTotalDistance] = useState(0);
  const [polygonArea, setPolygonArea] = useState(0);
  
  // Mobile accordion state
  const [mobileAccordions, setMobileAccordions] = useState({
    layers: false,
    styles: false,
    legend: false,
    locations: false,
  });
  
  // Mobile options panel state
  const [mobileOptionsOpen, setMobileOptionsOpen] = useState(false);

  const toggleMobileAccordion = (section) => {
    setMobileAccordions(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const mapTiles = {
    light: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    dark: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    satellite: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    terrain: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
  };

  const filteredLocations = locations.filter(loc =>
    loc.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle marker drag end
  const handleMarkerDragEnd = (locationId, newPosition) => {
    setLocations(locations.map(loc => 
      loc.id === locationId 
        ? { ...loc, lat: newPosition.lat, lng: newPosition.lng }
        : loc
    ));
  };

  const handleMapReady = (map) => {
    mapRef.current = map;
  };

  const handleZoomIn = () => {
    if (mapRef.current) {
      mapRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      mapRef.current.zoomOut();
    }
  };

  const handleResetView = () => {
    setMapCenter([51.3833, 6.4667]);
    setMapZoom(11);
  };

  const handleFullscreen = () => {
    const mapContainer = document.querySelector('.gis-map-container');
    if (!document.fullscreenElement) {
      mapContainer?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleExportPDF = () => {
    const pdf = new jsPDF('landscape');
    pdf.setFontSize(20);
    pdf.text('GIS Map Report', 20, 20);
    pdf.setFontSize(12);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, 20, 30);
    pdf.text(`Center: ${mapCenter[0].toFixed(4)}, ${mapCenter[1].toFixed(4)}`, 20, 40);
    pdf.text(`Zoom Level: ${mapZoom}`, 20, 50);
    
    pdf.setFontSize(14);
    pdf.text('Locations:', 20, 70);
    
    let yPos = 80;
    filteredLocations.forEach((loc, idx) => {
      pdf.setFontSize(10);
      pdf.text(`${idx + 1}. ${loc.name} - ${loc.type} (${loc.intensity}% intensity, ${loc.events} events)`, 25, yPos);
      yPos += 8;
    });
    
    pdf.save('gis-map-report.pdf');
  };

  const handleClearDrawings = () => {
    setMeasurePoints([]);
    setPolygonPoints([]);
    setDirectionPoints([]);
    setTotalDistance(0);
    setPolygonArea(0);
    setMeasureMode(false);
    setDrawMode(false);
    setDirectionsMode(false);
  };

  // Handle measure click
  const handleMeasureClick = (point) => {
    const newPoints = [...measurePoints, point];
    setMeasurePoints(newPoints);
    
    // Calculate total distance
    if (newPoints.length > 1) {
      let total = 0;
      for (let i = 1; i < newPoints.length; i++) {
        total += parseFloat(calculateDistance(
          newPoints[i-1][0], newPoints[i-1][1],
          newPoints[i][0], newPoints[i][1]
        ));
      }
      setTotalDistance(total.toFixed(2));
    }
  };

  // Handle polygon draw click
  const handleDrawClick = (point) => {
    const newPoints = [...polygonPoints, point];
    setPolygonPoints(newPoints);
    
    // Calculate approximate area if we have 3+ points
    if (newPoints.length >= 3) {
      const area = calculatePolygonArea(newPoints);
      setPolygonArea(area.toFixed(2));
    }
  };

  // Handle directions click
  const handleDirectionsClick = (point) => {
    if (directionPoints.length < 2) {
      const newPoints = [...directionPoints, point];
      setDirectionPoints(newPoints);
      
      if (newPoints.length === 2) {
        const dist = calculateDistance(
          newPoints[0][0], newPoints[0][1],
          newPoints[1][0], newPoints[1][1]
        );
        setTotalDistance(dist);
      }
    }
  };

  // Calculate polygon area using Shoelace formula (approximate in km²)
  const calculatePolygonArea = (points) => {
    if (points.length < 3) return 0;
    
    let area = 0;
    const n = points.length;
    
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      // Convert to approximate km
      const lat1 = points[i][0] * 111;
      const lng1 = points[i][1] * 111 * Math.cos(points[i][0] * Math.PI / 180);
      const lat2 = points[j][0] * 111;
      const lng2 = points[j][1] * 111 * Math.cos(points[j][0] * Math.PI / 180);
      
      area += lat1 * lng2;
      area -= lat2 * lng1;
    }
    
    return Math.abs(area / 2);
  };

  // Generate intermediate route points for directions (simple interpolation)
  const generateRoutePoints = useCallback(() => {
    if (directionPoints.length !== 2) return [];
    
    const [start, end] = directionPoints;
    const points = [start];
    
    // Add some intermediate waypoints for a more realistic route look
    const steps = 5;
    for (let i = 1; i < steps; i++) {
      const ratio = i / steps;
      // Use a deterministic offset based on position for consistency
      const seedOffset = Math.sin(ratio * 100) * 0.005;
      points.push([
        start[0] + (end[0] - start[0]) * ratio + seedOffset,
        start[1] + (end[1] - start[1]) * ratio + seedOffset * 0.5
      ]);
    }
    points.push(end);
    
    return points;
  }, [directionPoints]);

  // Calculate distance between two points
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return (R * c).toFixed(2);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <div className="gis-map-container flex flex-col lg:flex-row h-[calc(100vh-3.5rem)] lg:h-[calc(100vh-4rem)] relative bg-background">
      {/* Mobile Search & Options */}
      <div className="lg:hidden p-2 border-b border-border bg-card space-y-2">
        {/* Search Input - Above Options on Mobile */}
        <div className="relative">
          <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm"></i>
          <Input
            placeholder={t("searchLocations")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-9 text-sm"
          />
        </div>
        {/* Options Button */}
        <Button 
          variant="outline" 
          className="w-full h-10 text-sm font-medium"
          onClick={() => setMobileOptionsOpen(!mobileOptionsOpen)}
          data-testid="mobile-options-button"
        >
          <i className="fa-solid fa-sliders mr-2"></i>
          Options
          <i className={cn(
            "fa-solid ml-auto transition-transform duration-200",
            mobileOptionsOpen ? "fa-chevron-up" : "fa-chevron-down"
          )}></i>
        </Button>
      </div>

      {/* Left Panel - Controls (Hidden on mobile, shown via Options button) */}
      <div className={cn(
        "w-full lg:w-80 h-auto lg:h-full border-b lg:border-b-0 lg:border-r border-border bg-card flex flex-col z-20",
        "lg:block",
        mobileOptionsOpen ? "block" : "hidden lg:flex"
      )}>
        {/* Desktop search input only */}
        <div className="hidden lg:block p-3 lg:p-4 border-b border-border">
          <div className="relative">
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm"></i>
            <Input
              placeholder={t("searchLocations")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9 text-sm"
            />
          </div>
        </div>

        <ScrollArea className="flex-1 max-h-[30vh] lg:max-h-none">
          <div className="p-3 lg:p-4 space-y-1 lg:space-y-6">
            {/* Map Layers - Accordion on Mobile */}
            <div className="space-y-2 lg:space-y-3">
              {/* Mobile: Accordion Toggle */}
              <button
                className="lg:hidden w-full flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                onClick={() => toggleMobileAccordion('layers')}
                data-testid="mobile-layers-accordion"
              >
                <span className="text-xs font-medium text-foreground flex items-center gap-2">
                  <i className="fa-solid fa-layer-group text-primary"></i>
                  {t("mapLayers")}
                </span>
                <i className={cn(
                  "fa-solid text-xs text-muted-foreground transition-transform duration-200",
                  mobileAccordions.layers ? "fa-chevron-up" : "fa-chevron-down"
                )}></i>
              </button>

              {/* Desktop: Always visible header */}
              <h4 className="hidden lg:flex text-xs lg:text-sm font-medium text-foreground items-center gap-2">
                <i className="fa-solid fa-layer-group text-primary"></i>
                {t("mapLayers")}
              </h4>
              
              {/* Content - always visible on desktop, accordion on mobile */}
              <div className={cn(
                "space-y-2 overflow-hidden transition-all duration-200",
                "lg:block lg:max-h-none lg:opacity-100",
                mobileAccordions.layers ? "max-h-96 opacity-100" : "max-h-0 opacity-0 lg:max-h-none lg:opacity-100"
              )}>
                <div className="flex items-center justify-between">
                  <Label htmlFor="markers" className="text-xs lg:text-sm">{t("markers")}</Label>
                  <Switch
                    id="markers"
                    checked={layers.markers}
                    onCheckedChange={(checked) => setLayers({ ...layers, markers: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="heatmap" className="text-xs lg:text-sm">{t("intensityCircles")}</Label>
                  <Switch
                    id="heatmap"
                    checked={layers.heatmap}
                    onCheckedChange={(checked) => setLayers({ ...layers, heatmap: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="connections" className="text-xs lg:text-sm">{t("connections")}</Label>
                  <Switch
                    id="connections"
                    checked={layers.connections}
                    onCheckedChange={(checked) => setLayers({ ...layers, connections: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="clustering" className="text-xs lg:text-sm">{t("clustering")}</Label>
                  <Switch
                    id="clustering"
                    checked={clusteringEnabled}
                    onCheckedChange={setClusteringEnabled}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="editMode" className="text-xs lg:text-sm">{t("editMode")}</Label>
                  <Switch
                    id="editMode"
                    checked={editMode}
                    onCheckedChange={setEditMode}
                  />
                </div>
              </div>
            </div>

            <Separator className="hidden lg:block" />

            {/* Map Style - Accordion on Mobile */}
            <div className="space-y-2 lg:space-y-3">
              {/* Mobile: Accordion Toggle */}
              <button
                className="lg:hidden w-full flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                onClick={() => toggleMobileAccordion('styles')}
                data-testid="mobile-styles-accordion"
              >
                <span className="text-xs font-medium text-foreground flex items-center gap-2">
                  <i className="fa-solid fa-palette text-primary"></i>
                  {t("mapStyle")}
                </span>
                <i className={cn(
                  "fa-solid text-xs text-muted-foreground transition-transform duration-200",
                  mobileAccordions.styles ? "fa-chevron-up" : "fa-chevron-down"
                )}></i>
              </button>

              {/* Desktop: Always visible header */}
              <h4 className="hidden lg:flex text-xs lg:text-sm font-medium text-foreground items-center gap-2">
                <i className="fa-solid fa-palette text-primary"></i>
                {t("mapStyle")}
              </h4>

              {/* Content - always visible on desktop, accordion on mobile */}
              <div className={cn(
                "overflow-hidden transition-all duration-200",
                "lg:block lg:max-h-none lg:opacity-100",
                mobileAccordions.styles ? "max-h-20 opacity-100" : "max-h-0 opacity-0 lg:max-h-none lg:opacity-100"
              )}>
                <Select value={mapStyle} onValueChange={setMapStyle}>
                  <SelectTrigger className="h-9 text-xs lg:text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">{t("light")}</SelectItem>
                    <SelectItem value="dark">{t("dark")}</SelectItem>
                    <SelectItem value="satellite">{t("satellite")}</SelectItem>
                    <SelectItem value="terrain">{t("terrain")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator className="hidden lg:block" />

            {/* Legend - Accordion on Mobile */}
            <div className="space-y-2 lg:space-y-3">
              {/* Mobile: Accordion Toggle */}
              <button
                className="lg:hidden w-full flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                onClick={() => toggleMobileAccordion('legend')}
                data-testid="mobile-legend-accordion"
              >
                <span className="text-xs font-medium text-foreground flex items-center gap-2">
                  <i className="fa-solid fa-circle-info text-primary"></i>
                  {t("legend")}
                </span>
                <i className={cn(
                  "fa-solid text-xs text-muted-foreground transition-transform duration-200",
                  mobileAccordions.legend ? "fa-chevron-up" : "fa-chevron-down"
                )}></i>
              </button>

              {/* Desktop: Always visible header */}
              <h4 className="hidden lg:flex text-xs lg:text-sm font-medium text-foreground items-center gap-2">
                <i className="fa-solid fa-circle-info text-primary"></i>
                {t("legend")}
              </h4>

              {/* Content - always visible on desktop, accordion on mobile */}
              <div className={cn(
                "space-y-2 overflow-hidden transition-all duration-200",
                "lg:block lg:max-h-none lg:opacity-100",
                mobileAccordions.legend ? "max-h-40 opacity-100" : "max-h-0 opacity-0 lg:max-h-none lg:opacity-100"
              )}>
                {Object.entries(typeColors).map(([type, color]) => (
                  <div key={type} className="flex items-center gap-3">
                    <div 
                      className="h-3 w-3 lg:h-4 lg:w-4 rounded-full border-2 border-white shadow"
                      style={{ backgroundColor: color }}
                    ></div>
                    <span className="text-xs lg:text-sm capitalize">{t(type)}</span>
                  </div>
                ))}
              </div>
            </div>

            <Separator className="hidden lg:block" />

            {/* Locations List - Accordion on Mobile */}
            <div className="space-y-2 lg:space-y-3">
              {/* Mobile: Accordion Toggle */}
              <button
                className="lg:hidden w-full flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                onClick={() => toggleMobileAccordion('locations')}
                data-testid="mobile-locations-accordion"
              >
                <span className="text-xs font-medium text-foreground flex items-center gap-2">
                  <i className="fa-solid fa-location-dot text-primary"></i>
                  {t("locations")} ({filteredLocations.length})
                </span>
                <i className={cn(
                  "fa-solid text-xs text-muted-foreground transition-transform duration-200",
                  mobileAccordions.locations ? "fa-chevron-up" : "fa-chevron-down"
                )}></i>
              </button>

              {/* Desktop: Always visible header */}
              <h4 className="hidden lg:flex text-xs lg:text-sm font-medium text-foreground items-center gap-2">
                <i className="fa-solid fa-location-dot text-primary"></i>
                {t("locations")} ({filteredLocations.length})
              </h4>

              {/* Content - always visible on desktop, accordion on mobile */}
              <div className={cn(
                "space-y-2 overflow-hidden transition-all duration-200",
                "lg:block lg:max-h-none lg:opacity-100",
                mobileAccordions.locations ? "max-h-96 opacity-100" : "max-h-0 opacity-0 lg:max-h-none lg:opacity-100"
              )}>
                {filteredLocations.map((location) => (
                  <motion.div
                    key={location.id}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => {
                      setSelectedLocation(location);
                      setMapCenter([location.lat, location.lng]);
                      setMapZoom(14);
                    }}
                    className={cn(
                      "flex items-center gap-2 lg:gap-3 p-2 lg:p-3 rounded-lg cursor-pointer transition-colors",
                      selectedLocation?.id === location.id 
                        ? "bg-primary/10 border border-primary/30" 
                        : "bg-muted/50 hover:bg-muted"
                    )}
                  >
                    <div 
                      className="h-2 w-2 lg:h-3 lg:w-3 rounded-full shrink-0"
                      style={{ backgroundColor: typeColors[location.type] }}
                    ></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs lg:text-sm font-medium truncate">{location.name}</p>
                      <p className="text-[10px] lg:text-xs text-muted-foreground">{location.events} {t("events")}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px] lg:text-xs shrink-0">
                      {location.intensity}%
                    </Badge>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>

            <div className="p-3 lg:p-4 border-t border-border space-y-2">
              <Button variant="outline" className="w-full h-8 lg:h-9 text-xs lg:text-sm" onClick={handleExportPDF}>
                <i className="fa-solid fa-file-pdf mr-2"></i>
                {t("export")} PDF
              </Button>
            </div>
          </div>

      {/* Main Map Area */}
      <div className="flex-1 relative z-0">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          className="h-full w-full"
          style={{ background: mapStyle === "dark" ? "hsl(var(--card))" : "#f5f5f5", zIndex: 0 }}
        >
          <MapController center={mapCenter} zoom={mapZoom} onMapReady={handleMapReady} />
          <MapClickHandler 
            measureMode={measureMode}
            drawMode={drawMode}
            directionsMode={directionsMode}
            onMeasureClick={handleMeasureClick}
            onDrawClick={handleDrawClick}
            onDirectionsClick={handleDirectionsClick}
          />
          
          <TileLayer
            key={mapStyle}
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url={mapTiles[mapStyle]}
          />

          {/* Heatmap Circles */}
          {layers.heatmap && (
            <LayerGroup>
              {mockLocations.map((location) => (
                <Circle
                  key={`circle-${location.id}`}
                  center={[location.lat, location.lng]}
                  radius={location.intensity * 20}
                  pathOptions={{
                    color: typeColors[location.type],
                    fillColor: typeColors[location.type],
                    fillOpacity: 0.2,
                    weight: 1,
                  }}
                />
              ))}
            </LayerGroup>
          )}

          {/* Connections */}
          {layers.connections && (
            <LayerGroup>
              {mockLocations.slice(0, -1).map((loc, idx) => (
                <Polyline
                  key={`conn-${idx}`}
                  positions={[[loc.lat, loc.lng], [mockLocations[idx + 1].lat, mockLocations[idx + 1].lng]]}
                  pathOptions={{ color: "hsl(var(--primary))", weight: 1, dashArray: "5, 5", opacity: 0.5 }}
                />
              ))}
            </LayerGroup>
          )}

          {/* Measure Line with markers */}
          {measurePoints.length > 0 && (
            <LayerGroup>
              <Polyline
                positions={measurePoints}
                pathOptions={{ color: "#ff6b6b", weight: 3, dashArray: "10, 10" }}
              />
              {measurePoints.map((point, idx) => (
                <Circle
                  key={`measure-point-${idx}`}
                  center={point}
                  radius={50}
                  pathOptions={{ color: "#ff6b6b", fillColor: "#ff6b6b", fillOpacity: 1, weight: 2 }}
                />
              ))}
            </LayerGroup>
          )}

          {/* Polygon Drawing */}
          {polygonPoints.length > 0 && (
            <LayerGroup>
              {polygonPoints.length >= 3 && (
                <Polygon
                  positions={polygonPoints}
                  pathOptions={{ color: "#4CAF50", fillColor: "#4CAF50", fillOpacity: 0.3, weight: 2 }}
                />
              )}
              {polygonPoints.length < 3 && polygonPoints.length > 1 && (
                <Polyline
                  positions={polygonPoints}
                  pathOptions={{ color: "#4CAF50", weight: 2, dashArray: "5, 5" }}
                />
              )}
              {polygonPoints.map((point, idx) => (
                <Circle
                  key={`polygon-point-${idx}`}
                  center={point}
                  radius={50}
                  pathOptions={{ color: "#4CAF50", fillColor: "#4CAF50", fillOpacity: 1, weight: 2 }}
                />
              ))}
            </LayerGroup>
          )}

          {/* Directions Route */}
          {directionPoints.length > 0 && (
            <LayerGroup>
              {directionPoints.length === 2 && (
                <Polyline
                  positions={generateRoutePoints()}
                  pathOptions={{ color: "#2196F3", weight: 4, opacity: 0.8 }}
                />
              )}
              {directionPoints.map((point, idx) => (
                <Marker
                  key={`direction-point-${idx}`}
                  position={point}
                  icon={L.divIcon({
                    className: "direction-marker",
                    html: `<div style="
                      background-color: ${idx === 0 ? '#4CAF50' : '#F44336'};
                      width: 28px;
                      height: 28px;
                      border-radius: 50%;
                      border: 3px solid white;
                      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      color: white;
                      font-weight: bold;
                      font-size: 12px;
                    ">${idx === 0 ? 'A' : 'B'}</div>`,
                    iconSize: [28, 28],
                    iconAnchor: [14, 14],
                  })}
                />
              ))}
            </LayerGroup>
          )}

          {/* Markers - with optional clustering */}
          {layers.markers && (
            clusteringEnabled ? (
              <MarkerClusterGroup
                chunkedLoading
                spiderfyOnMaxZoom
                showCoverageOnHover={false}
                maxClusterRadius={50}
                iconCreateFunction={(cluster) => {
                  const count = cluster.getChildCount();
                  return L.divIcon({
                    html: `<div style="
                      background: linear-gradient(135deg, hsl(var(--primary)), hsl(var(--chart-2)));
                      width: ${Math.min(40 + count * 2, 60)}px;
                      height: ${Math.min(40 + count * 2, 60)}px;
                      border-radius: 50%;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      color: white;
                      font-weight: bold;
                      font-size: 14px;
                      border: 3px solid white;
                      box-shadow: 0 2px 10px rgba(0,0,0,0.3);
                    ">${count}</div>`,
                    className: 'custom-cluster',
                    iconSize: [Math.min(40 + count * 2, 60), Math.min(40 + count * 2, 60)],
                  });
                }}
              >
                {locations.map((location) => (
                  <Marker
                    key={location.id}
                    position={[location.lat, location.lng]}
                    icon={createCustomIcon(typeColors[location.type])}
                    draggable={editMode}
                    eventHandlers={{
                      click: () => setSelectedLocation(location),
                      dragend: (e) => {
                        const marker = e.target;
                        const position = marker.getLatLng();
                        handleMarkerDragEnd(location.id, position);
                      },
                    }}
                  >
                    <Popup>
                      <div className="p-2 min-w-[180px]">
                        <h4 className="font-semibold text-foreground">{location.name}</h4>
                        <p className="text-sm text-muted-foreground capitalize">{t(location.type)}</p>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-xs">{t("intensityScore")}</span>
                          <Badge variant="outline">{location.intensity}%</Badge>
                        </div>
                        {editMode && (
                          <p className="mt-2 text-xs text-muted-foreground italic">{t("dragToMove")}</p>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            ) : (
              locations.map((location) => (
                <Marker
                  key={location.id}
                  position={[location.lat, location.lng]}
                  icon={createCustomIcon(typeColors[location.type])}
                  draggable={editMode}
                  eventHandlers={{
                    click: () => setSelectedLocation(location),
                    dragend: (e) => {
                      const marker = e.target;
                      const position = marker.getLatLng();
                      handleMarkerDragEnd(location.id, position);
                    },
                  }}
                >
                  <Popup>
                    <div className="p-2 min-w-[180px]">
                      <h4 className="font-semibold text-foreground">{location.name}</h4>
                      <p className="text-sm text-muted-foreground capitalize">{t(location.type)}</p>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-xs">{t("intensityScore")}</span>
                        <Badge variant="outline">{location.intensity}%</Badge>
                      </div>
                      {editMode && (
                        <p className="mt-2 text-xs text-muted-foreground italic">{t("dragToMove")}</p>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))
            )
          )}
        </MapContainer>

        {/* Edit Mode Indicator */}
        {editMode && (
          <div className="absolute top-14 lg:top-4 left-1/2 transform -translate-x-1/2 z-30">
            <Card className="bg-yellow-500/90 backdrop-blur-sm border-yellow-600">
              <CardContent className="p-2 lg:p-3">
                <div className="flex items-center gap-2 text-white">
                  <i className="fa-solid fa-pen-to-square"></i>
                  <span className="text-xs lg:text-sm font-medium">{t("editModeActive")}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Toolbar - Top Right */}
        <TooltipProvider>
          <div className="absolute top-14 lg:top-4 right-2 lg:right-4 z-30 flex flex-col gap-2">
            <Card className="bg-card/95 backdrop-blur-sm">
              <CardContent className="p-1.5 lg:p-2 flex flex-col gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 lg:h-8 lg:w-8" onClick={handleZoomIn}>
                      <i className="fa-solid fa-plus text-xs lg:text-sm"></i>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">{t("zoomIn")}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 lg:h-8 lg:w-8" onClick={handleZoomOut}>
                      <i className="fa-solid fa-minus text-xs lg:text-sm"></i>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">{t("zoomOut")}</TooltipContent>
                </Tooltip>
                <Separator className="my-1" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 lg:h-8 lg:w-8" onClick={handleResetView}>
                      <i className="fa-solid fa-location-crosshairs text-xs lg:text-sm"></i>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">{t("resetView")}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 lg:h-8 lg:w-8" onClick={handleFullscreen}>
                      <i className={cn("fa-solid text-xs lg:text-sm", isFullscreen ? "fa-compress" : "fa-expand")}></i>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">{t("fullscreen")}</TooltipContent>
                </Tooltip>
                <Separator className="my-1" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant={measureMode ? "default" : "ghost"} 
                      size="icon" 
                      className="h-7 w-7 lg:h-8 lg:w-8"
                      onClick={() => { setMeasureMode(!measureMode); setDrawMode(false); setDirectionsMode(false); }}
                    >
                      <i className="fa-solid fa-ruler text-xs lg:text-sm"></i>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">{t("measureDistance")}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant={drawMode ? "default" : "ghost"} 
                      size="icon" 
                      className="h-7 w-7 lg:h-8 lg:w-8"
                      onClick={() => { setDrawMode(!drawMode); setMeasureMode(false); setDirectionsMode(false); }}
                    >
                      <i className="fa-solid fa-draw-polygon text-xs lg:text-sm"></i>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">{t("drawPolygon")}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant={directionsMode ? "default" : "ghost"} 
                      size="icon" 
                      className="h-7 w-7 lg:h-8 lg:w-8"
                      onClick={() => { setDirectionsMode(!directionsMode); setMeasureMode(false); setDrawMode(false); }}
                    >
                      <i className="fa-solid fa-route text-xs lg:text-sm"></i>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">{t("directions")}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 lg:h-8 lg:w-8" onClick={handleClearDrawings}>
                      <i className="fa-solid fa-eraser text-xs lg:text-sm"></i>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">{t("clearDrawings")}</TooltipContent>
                </Tooltip>
              </CardContent>
            </Card>
          </div>
        </TooltipProvider>

        {/* Active Tool Info Panel */}
        {(measureMode || drawMode || directionsMode) && (
          <div className="absolute top-14 lg:top-4 left-1/2 transform -translate-x-1/2 z-30">
            <Card className="bg-card/95 backdrop-blur-sm">
              <CardContent className="p-2 lg:p-3">
                <div className="flex items-center gap-3">
                  {measureMode && (
                    <>
                      <i className="fa-solid fa-ruler text-primary"></i>
                      <div className="text-xs lg:text-sm">
                        <span className="text-muted-foreground">{t("measureDistance")}: </span>
                        <span className="font-semibold">{totalDistance} km</span>
                        <span className="text-muted-foreground ml-2">({measurePoints.length} {t("points")})</span>
                      </div>
                    </>
                  )}
                  {drawMode && (
                    <>
                      <i className="fa-solid fa-draw-polygon text-green-500"></i>
                      <div className="text-xs lg:text-sm">
                        <span className="text-muted-foreground">{t("drawPolygon")}: </span>
                        <span className="font-semibold">{polygonArea} km²</span>
                        <span className="text-muted-foreground ml-2">({polygonPoints.length} {t("points")})</span>
                      </div>
                    </>
                  )}
                  {directionsMode && (
                    <>
                      <i className="fa-solid fa-route text-blue-500"></i>
                      <div className="text-xs lg:text-sm">
                        <span className="text-muted-foreground">{t("directions")}: </span>
                        {directionPoints.length < 2 ? (
                          <span className="font-semibold">{t("clickToSetPoints")}</span>
                        ) : (
                          <span className="font-semibold">{totalDistance} km</span>
                        )}
                        <span className="text-muted-foreground ml-2">({directionPoints.length}/2 {t("points")})</span>
                      </div>
                    </>
                  )}
                  <Button variant="ghost" size="icon" className="h-6 w-6 ml-2" onClick={handleClearDrawings}>
                    <i className="fa-solid fa-xmark text-xs"></i>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stats Overlay - Bottom Left */}
        <div className="absolute bottom-20 lg:bottom-24 left-2 lg:left-4 z-30">
          <Card className="bg-card/95 backdrop-blur-sm">
            <CardContent className="p-2 lg:p-4">
              <div className="grid grid-cols-3 gap-3 lg:gap-6">
                <div className="text-center">
                  <p className="text-lg lg:text-2xl font-bold text-primary">{mockLocations.length}</p>
                  <p className="text-[10px] lg:text-xs text-muted-foreground">{t("locations")}</p>
                </div>
                <div className="text-center">
                  <p className="text-lg lg:text-2xl font-bold text-chart-2">157</p>
                  <p className="text-[10px] lg:text-xs text-muted-foreground">{t("events")}</p>
                </div>
                <div className="text-center">
                  <p className="text-lg lg:text-2xl font-bold text-chart-4">3</p>
                  <p className="text-[10px] lg:text-xs text-muted-foreground">{t("hotspots")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Panel - Location Details (No Animation) */}
      {selectedLocation && (
        <div className="absolute lg:relative right-0 h-full w-80 border-l border-border bg-card overflow-hidden z-20">
          <div className="w-80 h-full flex flex-col">
            <div className="p-3 lg:p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-foreground text-sm lg:text-base">{t("locationDetails")}</h3>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7 lg:h-8 lg:w-8"
                  onClick={() => setSelectedLocation(null)}
                >
                  <i className="fa-solid fa-xmark text-sm"></i>
                </Button>
              </div>
            </div>

            <ScrollArea className="flex-1 p-3 lg:p-4">
              <div className="space-y-4 lg:space-y-6">
                {/* Location Header */}
                <div className="flex items-center gap-3 lg:gap-4">
                  <div 
                    className="flex h-12 w-12 lg:h-14 lg:w-14 items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${typeColors[selectedLocation.type]}20` }}
                  >
                    <i 
                      className="fa-solid fa-location-dot text-xl lg:text-2xl"
                      style={{ color: typeColors[selectedLocation.type] }}
                    ></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground text-sm lg:text-base">{selectedLocation.name}</h4>
                    <Badge 
                      variant="outline" 
                      className="mt-1 capitalize text-xs"
                      style={{ 
                        backgroundColor: `${typeColors[selectedLocation.type]}20`,
                        borderColor: typeColors[selectedLocation.type]
                      }}
                    >
                      {t(selectedLocation.type)}
                    </Badge>
                  </div>
                </div>

                  <Separator />

                  {/* Coordinates */}
                  <div className="space-y-2 lg:space-y-3">
                    <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t("coordinates")}
                    </h5>
                    <div className="grid grid-cols-2 gap-2 lg:gap-3">
                      <div className="p-2 lg:p-3 rounded-lg bg-muted/50">
                        <p className="text-[10px] lg:text-xs text-muted-foreground">{t("latitude")}</p>
                        <p className="font-mono text-xs lg:text-sm">{selectedLocation.lat.toFixed(4)}</p>
                      </div>
                      <div className="p-2 lg:p-3 rounded-lg bg-muted/50">
                        <p className="text-[10px] lg:text-xs text-muted-foreground">{t("longitude")}</p>
                        <p className="font-mono text-xs lg:text-sm">{selectedLocation.lng.toFixed(4)}</p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Activity Metrics */}
                  <div className="space-y-2 lg:space-y-3">
                    <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t("activityMetrics")}
                    </h5>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 lg:p-3 rounded-lg bg-muted/50">
                        <span className="text-xs lg:text-sm">{t("intensityScore")}</span>
                        <Badge variant={selectedLocation.intensity > 70 ? "destructive" : "secondary"} className="text-xs">
                          {selectedLocation.intensity}%
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between p-2 lg:p-3 rounded-lg bg-muted/50">
                        <span className="text-xs lg:text-sm">{t("totalEvents")}</span>
                        <span className="font-semibold text-sm">{selectedLocation.events}</span>
                      </div>
                      <div className="flex items-center justify-between p-2 lg:p-3 rounded-lg bg-muted/50">
                        <span className="text-xs lg:text-sm">{t("lastActivity")}</span>
                        <span className="text-xs lg:text-sm text-muted-foreground">2 {t("hoursAgo")}</span>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Recent Events */}
                  <div className="space-y-2 lg:space-y-3">
                    <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t("recentEvents")}
                    </h5>
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-2 lg:gap-3 p-2 lg:p-3 rounded-lg bg-muted/50">
                          <div className="h-2 w-2 rounded-full bg-primary"></div>
                          <div className="flex-1">
                            <p className="text-xs lg:text-sm font-medium">{t("event")} {i}</p>
                            <p className="text-[10px] lg:text-xs text-muted-foreground">{i} {t("hoursAgo")}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </ScrollArea>

              <div className="p-3 lg:p-4 border-t border-border">
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 h-8 lg:h-9 text-xs lg:text-sm">
                    <i className="fa-solid fa-route mr-1 lg:mr-2"></i>
                    {t("directions")}
                  </Button>
                  <Button className="flex-1 h-8 lg:h-9 text-xs lg:text-sm" onClick={() => {
                    setMapCenter([selectedLocation.lat, selectedLocation.lng]);
                    setMapZoom(16);
                  }}>
                    <i className="fa-solid fa-crosshairs mr-1 lg:mr-2"></i>
                    {t("focus")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
